import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authScopes, publicClientApplication } from 'src/app/constants/config/authConfig';
import { config } from 'src/app/constants/config/config';
import {
    buildAuthMessage,
    buildNavigateToMessage,
    isAdminEmbedMessage,
} from './adminEmbedProtocol';

interface AdminEmbedProps {
    /** Embed subpath derived from the React route (e.g. "contracts" → iframe /embed/contracts). */
    path: string;
    /** Reports in-iframe navigation up, as a base-relative embed path (e.g. "/embed/contracts"). */
    onNavigate?: (embedPath: string) => void;
}

/**
 * Hosts the standalone Blazor Admin app in an iframe and speaks the host side of the postMessage
 * handshake: supplies the MSAL ID token on `ready` / `token-request`, and relays the
 * iframe's `navigate` messages up via {@link AdminEmbedProps.onNavigate}. Origins are pinned in both
 * directions.
 *
 * Host-driven navigation (deep-link, back/forward) goes over `navigate-to` rather than reassigning
 * `src`, which would append an entry to the tab's joint session history — truncating the forward
 * stack and trapping the back button inside the embed.
 */
const AdminEmbed = ({ path, onNavigate }: AdminEmbedProps) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const adminOrigin = useMemo(() => {
        try {
            return new URL(config.ADMIN_UI_URL ?? '').origin;
        } catch {
            return '';
        }
    }, []);

    // Mount-time value of `path`, deliberately not updated on re-render.
    const [iframeSrcPath] = useState(path);

    // What the iframe shows, or is about to. Written from both directions so neither side re-issues
    // a navigation the other one originated.
    const iframePathRef = useRef<string>(normalizeEmbedPath(path));
    const readyRef = useRef(false);
    const pendingPathRef = useRef<string | null>(null);

    const acquireAuth = useCallback(async () => {
        const accounts = publicClientApplication.getAllAccounts();
        if (accounts.length === 0) {
            return null;
        }
        const tokenRequest = { scopes: authScopes, account: accounts[0] };

        let result: AuthenticationResult;
        try {
            result = await publicClientApplication.acquireTokenSilent(tokenRequest);
        } catch (error) {
            // Embed mode never redirects to login on its own, so an expired session has to be
            // rescued from here — otherwise the iframe just times out and reuses a dead token.
            if (error instanceof InteractionRequiredAuthError) {
                await publicClientApplication.acquireTokenRedirect(tokenRequest);
            }
            return null;
        }

        // Send the ID token (matches the axios interceptor the backend already accepts). Its expiry
        // is the `exp` claim of the ID token, not the access-token expiry.
        const claims = result.idTokenClaims as
            | { exp?: number; roles?: string[] | string }
            | undefined;
        const expiresAt = claims?.exp
            ? new Date(claims.exp * 1000).toISOString()
            : new Date(Date.now() + 30 * 60 * 1000).toISOString();
        return { token: result.idToken, expiresAt, roles: toRoles(claims?.roles) };
    }, []);

    const pushToken = useCallback(async () => {
        const target = iframeRef.current?.contentWindow;
        if (!target || !adminOrigin) {
            return;
        }
        const auth = await acquireAuth();
        if (auth) {
            target.postMessage(
                buildAuthMessage(auth.token, auth.expiresAt, auth.roles),
                adminOrigin
            );
        }
    }, [acquireAuth, adminOrigin]);

    // Nothing is listening before the handshake, so a pre-`ready` request is parked and flushed below.
    const requestIframePath = useCallback(
        (embedPath: string) => {
            const target = iframeRef.current?.contentWindow;
            if (!readyRef.current || !target || !adminOrigin) {
                pendingPathRef.current = embedPath;
                return;
            }
            pendingPathRef.current = null;
            target.postMessage(buildNavigateToMessage(embedPath), adminOrigin);
        },
        [adminOrigin]
    );

    // Message listener: only trust the pinned admin origin and our channel/version.
    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== adminOrigin || !isAdminEmbedMessage(event.data)) {
                return;
            }
            const message = event.data;
            switch (message.kind) {
                case 'ready':
                    readyRef.current = true;
                    void pushToken();
                    // A back/forward press during boot may have moved the target since mount.
                    if (pendingPathRef.current !== null) {
                        requestIframePath(pendingPathRef.current);
                    }
                    break;
                case 'token-request':
                    void pushToken();
                    break;
                case 'navigate': {
                    const reported = normalizeEmbedPath(message.path);
                    // The iframe also emits `navigate` when the browser restores its own history entry
                    // during a traversal; pushing that into the router would truncate the forward stack.
                    if (reported === iframePathRef.current) {
                        break;
                    }
                    iframePathRef.current = reported;
                    onNavigate?.(message.path);
                    break;
                }
                default:
                    break;
            }
        };

        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [adminOrigin, onNavigate, pushToken, requestIframePath]);

    // React-driven navigation (deep-link, back/forward); iframe-originated changes already match.
    useEffect(() => {
        const normalized = normalizeEmbedPath(path);
        if (normalized !== iframePathRef.current) {
            iframePathRef.current = normalized;
            requestIframePath(normalized);
        }
    }, [path, requestIframePath]);

    if (!adminOrigin) {
        return <div>Admin UI URL is not configured (REACT_APP_ADMIN_UI_URL).</div>;
    }

    // Build from the origin (no trailing slash) so a configured URL like "http://host:5095/" does
    // not produce a double slash ("//embed/...").
    const src = `${adminOrigin}/embed/${stripLeadingSlashes(iframeSrcPath)}`;

    return (
        <iframe
            ref={iframeRef}
            title="ETBaaS Admin"
            src={src}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        />
    );
};

const toRoles = (roles: string[] | string | undefined): string[] => {
    if (Array.isArray(roles)) {
        return roles.filter((role) => typeof role === 'string');
    }
    return typeof roles === 'string' ? [roles] : [];
};

const stripLeadingSlashes = (value: string) => value.replace(/^\/+/, '');

// Reduce both "contracts" and "/embed/contracts" to a comparable "contracts".
const normalizeEmbedPath = (value: string) => stripLeadingSlashes(value).replace(/^embed\/?/, '');

export default AdminEmbed;
